import type {
  FandomLegacyContextTargeting,
  FeaturedVideoConfig,
} from "common-types";

const PAGE_TYPE_IC_SELECTOR = ".mw-parser-output > h2";

/**
 * Checks if the featured video is on the page.
 * Returns `true` only if targeting indicates a featured video and a video can be played.
 */
export function hasFeaturedVideoOnPage(
  targeting?: FandomLegacyContextTargeting,
): boolean {
  const contextTargeting = getContextTargeting(targeting) ?? {};
  return !!(contextTargeting.hasFeaturedVideo && window.canPlayVideo?.());
}

/**
 * Checks if the incontent video is on the page.
 * There is some confusion around what the selector is trying to check for, but
 * seems to be incontent-player, though that may have changed over time in ad engine 3 and the
 * selector is fairly generic.
 * @returns true if featured video is not on the page and the selector is found
 */
export function isIncontentPlayerApplicable(
  targeting?: FandomLegacyContextTargeting,
): boolean {
  return (
    !hasFeaturedVideoOnPage(targeting) &&
    !!document.querySelector(PAGE_TYPE_IC_SELECTOR)
  );
}

/**
 * Returns video related data from the media wiki config.
 */
export function getWikiTargetedVideoInfo(): FeaturedVideoConfig | null {
  return (
    window.mw?.config?.get("wgArticleFeaturedVideo") ??
    window.RLCONF?.wgArticleFeaturedVideo ??
    null
  );
}

/**
 * This function is used to get the context targeting from the fandom context object.
 * It will first check if the targeting is passed in as an argument, and if not,
 * it will return the targeting from the `window.ads.context` object.
 */
function getContextTargeting(
  targeting?: FandomLegacyContextTargeting,
): FandomLegacyContextTargeting | undefined {
  return targeting ?? window.ads?.context.targeting;
}
